/**
 * Abstraction over the built-in fetch API. This removes the need for jQuery ajax. See https://developer.mozilla.org/en-US/docs/Web/API/Fetch_API
 * A polyfill for fetch is necessary to support IE11. See https://caniuse.com/#feat=fetch
 *
 * Why not use window.fetch directly? This abstraction has some benefits:
 *  - when we write integration tests, we don't need to setup a fake network, we can just fake this interface
 *  - using this abstraction, we can set default options, such as 'includeCredentials' which allows for authenticated API calls
 */
import { AbortError } from "../types/AbortError";
import { hasRanOutOfMemory, OutOfMemoryError, throwHasRanOutOfMemory } from "../types/OutOfMemoryError";
import { OutOfMemoryResponseDecorator } from "../types/Response";
import { ServerError } from "../exceptions/ServerError";
import { errorify } from "../errors/errorify";

export interface IFetcher {
	fetch(request: RequestInfo, options?: RequestInit): Promise<Response>;

	getJSON<TResponseBody>(request: RequestInfo, options?: RequestInit): Promise<TResponseBody>;

	postJSON<TResponseBody>(request: RequestInfo, requestBody?: object | any[], options?: RequestInit): Promise<TResponseBody>;

	get(request: RequestInfo, options?: RequestInit): Promise<boolean>;

	post<TRequestBody>(request: RequestInfo, requestBody?: TRequestBody, options?: RequestInit): Promise<boolean>;
}

export const defaultHeaders: HeadersInit = {
	// disable caching mechanisms, especially important for IE11 which caches aggressively
	pragma: "no-cache",
	"cache-control": "no-cache",
};

export const defaultGetJsonHeaders: HeadersInit = {
	...defaultHeaders,
	accept: "application/json",
	"x-requested-with": "XMLHttpRequest",
};

export const defaultPostJsonHeaders: HeadersInit = {
	...defaultHeaders,
	accept: "application/json",
	"content-type": "application/json",
	method: "POST",
	"x-requested-with": "XMLHttpRequest",
};

/**
 * The default options that will be passed to any call to the Fetch API.
 */
export const defaultOptions: Partial<RequestInit> = {
	// pass along cookie credentials when sending AJAX calls
	credentials: "include",
	headers: defaultHeaders,
};

export const defaultGetJsonOptions: Partial<RequestInit> = {
	...defaultOptions,
	method: "GET",
	headers: defaultGetJsonHeaders,
};

export const defaultPostJsonOptions: Partial<RequestInit> = {
	...defaultOptions,
	method: "POST",
	headers: defaultPostJsonHeaders,
};

export class JsonError extends Error {
	public kind = "json-error" as const;

	constructor(
		public requestInfo: RequestInfo,
		public httpCode: number,
		public statusText: string,
		public content: any,
	) {
		super(`${requestInfo} returned ${httpCode} ${statusText}\n${content.toString()}`);
	}
}

export const isJsonError = (error: unknown): error is JsonError => {
	return typeof error === "object" && error !== null && typeof error["kind"] !== "undefined" && error["kind"] === "json-error";
};

export type FetchProvider = {
	fetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response>;
};

export class Fetcher implements IFetcher {
	constructor(private readonly fetchProvider: FetchProvider = typeof self === "undefined" ? global : self) {}

	async fetch(request: RequestInfo, options?: RequestInit): Promise<Response> {
		options = options ? { ...defaultOptions, ...options, headers: { ...defaultHeaders, ...options.headers } } : defaultOptions;

		try {
			const result = await this.fetchProvider.fetch(request, options);
			return new OutOfMemoryResponseDecorator(result);
		} catch (e) {
			if (options?.signal && options?.signal.aborted) throw new AbortError();

			throwHasRanOutOfMemory(e);

			throw e;
		}
	}

	async getJSON<TResponseBody>(request: RequestInfo, options?: RequestInit): Promise<TResponseBody> {
		options = options ? { ...defaultGetJsonOptions, ...options, headers: { ...defaultGetJsonHeaders, ...options.headers } } : defaultGetJsonOptions;

		const response = await this.fetchProvider.fetch(request, options);

		if (response.ok) return await response.json();

		const responseText = await response.text();
		if (response.status && response.status >= 500 && response.status <= 599) {
			throw new ServerError(`${request} returned ${response.status} ${response.statusText}`, response.status, response.url, response.statusText, responseText);
		}

		throw new JsonError(request, response.status, response.statusText, responseText);
	}

	get(request: RequestInfo, options?: RequestInit): Promise<boolean> {
		options = options ? { ...defaultGetJsonOptions, ...options, headers: { ...defaultGetJsonHeaders, ...options.headers } } : defaultGetJsonOptions;

		return new Promise<boolean>((resolve, reject) => {
			this.fetchProvider
				.fetch(request, options)
				.then(response => resolve(response.ok))
				.catch(error => {
					if (options?.signal && options?.signal.aborted) reject(new AbortError());
					if (hasRanOutOfMemory(error)) reject(new OutOfMemoryError(error.message));
					reject(errorify(error));
				});
		});
	}

	async postJSON<TRequestBody, TResponseBody>(request: RequestInfo, requestBody?: TRequestBody, options?: RequestInit): Promise<TResponseBody> {
		const body = requestBody ? { body: JSON.stringify(requestBody) } : {};
		options = options
			? { ...defaultPostJsonOptions, ...options, ...body, headers: { ...defaultPostJsonHeaders, ...options.headers } }
			: { ...defaultPostJsonOptions, ...body, headers: defaultPostJsonHeaders };

		let response: Response;

		try {
			response = await this.fetchProvider.fetch(request, options);
		} catch (e) {
			if (options?.signal && options?.signal.aborted) throw new AbortError();

			throwHasRanOutOfMemory(e);

			throw errorify(e);
		}

		if (response.ok) return await response.json();

		const responseText = await response.text();

		if (response.status && response.status >= 500 && response.status <= 599) {
			throw new ServerError(`${request} returned ${response.status} ${response.statusText}`, response.status, response.url, response.statusText, responseText);
		}
		throw new Error(`${request} returned ${response.status} ${response.statusText}\n${responseText}`);
	}

	async post<TRequestBody>(request: RequestInfo, requestBody?: TRequestBody, options?: RequestInit): Promise<boolean> {
		const body = requestBody ? { body: JSON.stringify(requestBody) } : {};
		options = options
			? { ...defaultPostJsonOptions, ...options, ...body, headers: { ...defaultPostJsonHeaders, ...options.headers } }
			: { ...defaultPostJsonOptions, ...body, headers: defaultPostJsonHeaders };

		let response: Response;

		try {
			response = await this.fetchProvider.fetch(request, options);
		} catch (e) {
			if (options?.signal && options?.signal.aborted) throw new AbortError();

			throwHasRanOutOfMemory(e);

			throw errorify(e);
		}

		if (response.ok) return true;

		const responseJson = await response.json();
		throw new JsonError(request, response.status, response.statusText, responseJson);
	}
}
